import { useIntl } from 'react-intl';

import { useAnalytics } from '@/hooks/analytics/useAnalytics';
import { currencyStringFromCents } from '@/utils/helpers';

import { TeamsPageName } from '../../../types';
import { calculatingSelectedContractedLives } from '../../../utils';
import { PaymentHr } from '../../styles';
import messages from './messages';
import {
	BodyText,
	BodyTextBold,
	BodyTextLink,
	Container,
	Header,
	LeftAlignedContainer,
	RightAlignedContainer,
	Row,
} from './styles';

interface PaymentOverviewProps {
	finalPricing: {
		subtotal: number;
		tax?: number;
		discount: number;
		totalDue: number;
		couponDuration: string;
	};
	selectedCoveredLives: number;
	setActivePage: (page: TeamsPageName) => Promise<void>;
	shouldShowTaxAmount: boolean;
	isLoadingTaxes: boolean;
	context: {
		location: string;
		companySize: string;
		variant?: string;
	};
}

const PaymentOverview: React.FC<PaymentOverviewProps> = ({
	finalPricing,
	selectedCoveredLives,
	setActivePage,
	shouldShowTaxAmount,
	isLoadingTaxes,
	context: { location, variant, companySize },
}) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const { subtotal, tax, discount, totalDue } = finalPricing;

	const onClick = async (): Promise<void> => {
		await setActivePage('coveredLives');
		logEvent('Teams : Checkout : Covered Lives: Edit : Clicked', {
			companySize: companySize,
			coveredLives: selectedCoveredLives,
			selectedContractedLives: calculatingSelectedContractedLives(selectedCoveredLives),
			variant: variant ?? '',
			location,
		});
	};

	return (
		<Container>
			<PaymentHr />
			<Header>{formatMessage(messages.teamPlan)}</Header>
			<br />
			<Row>
				<BodyText>{formatMessage(messages.individuals, { selectedCoveredLives })}</BodyText>
				<BodyTextLink onClick={onClick}>{formatMessage(messages.edit)}</BodyTextLink>
			</Row>
			<br />
			<PaymentHr />
			<Row>
				<BodyText>{formatMessage(messages.subtotal)}</BodyText>
				<BodyText data-testid="self-serve-billing-subtotal">{currencyStringFromCents(subtotal)}</BodyText>
			</Row>
			{discount > 0 && (
				<Row>
					<BodyText>{formatMessage(messages.discount)}</BodyText>
					<BodyText data-testid="self-serve-billing-discount">-{currencyStringFromCents(discount)}</BodyText>
				</Row>
			)}
			<Row>
				<LeftAlignedContainer>
					<BodyText>{formatMessage(messages.tax)}</BodyText>
					{/*<Tooltip placement="top" content={formatMessage(messages.taxTooltip)} maxWidth={210}>
						<ExplainerInfo />
					</Tooltip>*/}
				</LeftAlignedContainer>
				<RightAlignedContainer>
					<BodyText data-testid="self-serve-billing-tax">
						{shouldShowTaxAmount
							? isLoadingTaxes
								? formatMessage(messages.calculatingTaxes)
								: currencyStringFromCents(tax)
							: formatMessage(messages.enterBillingDetails)}
					</BodyText>
				</RightAlignedContainer>
			</Row>
			<br />
			<Row>
				<BodyTextBold>{formatMessage(messages.totalPerYear)}</BodyTextBold>
				<RightAlignedContainer>
					<BodyTextBold data-testid="self-serve-billing-total">
						{currencyStringFromCents(totalDue)}
					</BodyTextBold>
				</RightAlignedContainer>
			</Row>
		</Container>
	);
};

export default PaymentOverview;
